import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Modal from '../components/Modal';
import axios from '../services/axios';
import InformeConsumoPanel from '../components/InformeConsumoPanel';
import { formatApiDate, getTodayDateInputValue, isValidDateInputValue } from '../utils/date';

const MedicionesAdmin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [zonaFilter, setZonaFilter] = useState('todas');
  const [estadoFilter, setEstadoFilter] = useState('todos');
  const [ordenamiento, setOrdenamiento] = useState('padron');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [modoExpress, setModoExpress] = useState(false);
  const [modoEditarFechas, setModoEditarFechas] = useState(false);
  const [fechaLecturaMasiva, setFechaLecturaMasiva] = useState(getTodayDateInputValue());
  const [observacionesMasivas, setObservacionesMasivas] = useState('');
  const [lecturasMasivas, setLecturasMasivas] = useState({});
  const [erroresMasivos, setErroresMasivos] = useState({});
  const [ultimasLecturas, setUltimasLecturas] = useState({});
  const [guardandoMasivo, setGuardandoMasivo] = useState(false);
  const [cargandoUltimas, setCargandoUltimas] = useState(false);
  const [editarFechasZona, setEditarFechasZona] = useState('');
  const [editarFechasDesde, setEditarFechasDesde] = useState('');
  const [editarFechasHasta, setEditarFechasHasta] = useState('');
  const [editarFechasNueva, setEditarFechasNueva] = useState('');
  const [editarFechasPreview, setEditarFechasPreview] = useState(null);
  const [cargandoPreviewFechas, setCargandoPreviewFechas] = useState(false);
  const [guardandoFechas, setGuardandoFechas] = useState(false);
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null,
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  useEffect(() => {
    filtrarClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientes, searchTerm, zonaFilter, estadoFilter, ordenamiento]);

  useEffect(() => {
    setItemsPerPage(modoExpress ? 25 : 10);
    setCurrentPage(1);
  }, [modoExpress]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [clientesRes, zonasRes] = await Promise.all([
        axios.get('/usuarios'),
        axios.get('/zonas'),
      ]);

      const soloClientes = (clientesRes.data || []).filter(u => u.rol === 'cliente');
      setClientes(soloClientes);
      setZonas(zonasRes.data || []);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'No se pudieron cargar los clientes y zonas.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
    } finally {
      setLoading(false);
    }
  };

  const filtrarClientes = () => {
    let filtered = [...clientes];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        c =>
          c.nombre?.toLowerCase().includes(term) ||
          c.padron?.toLowerCase().includes(term) ||
          c.cuit?.toLowerCase().includes(term) ||
          c.direccion?.toLowerCase().includes(term)
      );
    }

    if (zonaFilter !== 'todas') {
      filtered = filtered.filter(c => c.zona?.id === parseInt(zonaFilter));
    }

    if (estadoFilter !== 'todos') {
      if (estadoFilter === 'activo') {
        filtered = filtered.filter(c => c.activo !== false);
      } else if (estadoFilter === 'inactivo') {
        filtered = filtered.filter(c => c.activo === false);
      } else if (estadoFilter === 'baja') {
        filtered = filtered.filter(c => c.servicio_dado_de_baja);
      }
    }

    if (ordenamiento === 'padron') {
      filtered.sort((a, b) => {
        const parsePadron = (p) => {
          if (!p) return [0, 0];
          const parts = String(p).split('-');
          return [parseInt(parts[0]) || 0, parseInt(parts[1]) || 0];
        };
        const [a1, a2] = parsePadron(a.padron);
        const [b1, b2] = parsePadron(b.padron);
        return a1 !== b1 ? a1 - b1 : a2 - b2;
      });
    } else if (ordenamiento === 'nombre') {
      filtered.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es'));
    }

    setFilteredClientes(filtered);
    setCurrentPage(1);
  };

  const clientePorMedidor = useMemo(() => {
    const mapa = {};
    clientes.forEach((cliente) => {
      const medidorId = cliente?.medidor?.id;
      if (medidorId) {
        mapa[medidorId] = cliente;
      }
    });
    return mapa;
  }, [clientes]);

  const lecturasPendientes = useMemo(() => {
    return Object.values(lecturasMasivas).filter(
      (valor) => valor !== '' && valor !== null && typeof valor !== 'undefined'
    ).length;
  }, [lecturasMasivas]);

  const normalizarLecturaInput = (value) => {
    if (value === null || typeof value === 'undefined') return '';
    const texto = String(value);
    return texto.replace(/[^\d]/g, '');
  };

  const handleLecturaMasivaChange = (medidorId, value) => {
    const limpio = normalizarLecturaInput(value);
    setLecturasMasivas((prev) => ({
      ...prev,
      [medidorId]: limpio,
    }));
    setErroresMasivos((prev) => {
      if (!prev[medidorId]) {
        return prev;
      }
      const siguiente = { ...prev };
      delete siguiente[medidorId];
      return siguiente;
    });
  };

  const cargarUltimasLecturas = async (medidorIds) => {
    if (!medidorIds?.length) {
      return;
    }
    setCargandoUltimas(true);
    try {
      const response = await axios.get('/lecturas/ultimas', {
        params: { medidorIds: medidorIds.join(',') },
      });
      const data = response.data || [];
      const mapa = {};
      data.forEach((item) => {
        if (!item?.medidorId) return;
        mapa[item.medidorId] = {
          lecturaActual: item.lecturaActual,
          fechaLectura: item.fechaLectura,
        };
      });
      setUltimasLecturas((prev) => ({ ...prev, ...mapa }));
    } catch (error) {
      console.error('Error al cargar ultimas lecturas:', error);
    } finally {
      setCargandoUltimas(false);
    }
  };

  const obtenerBloqueoCliente = (cliente) => {
    if (!cliente?.medidor?.id) {
      return 'Sin medidor';
    }
    if (cliente.servicio_dado_de_baja) {
      return 'Baja servicio';
    }
    if (cliente.activo === false) {
      return 'Inactivo';
    }
    return null;
  };

  const limpiarLecturasMasivas = () => {
    setLecturasMasivas({});
    setErroresMasivos({});
  };

  const construirPayloadMasivo = () => {
    const items = [];
    const invalidos = [];
    Object.entries(lecturasMasivas).forEach(([medidorId, valor]) => {
      if (valor === '' || valor === null || typeof valor === 'undefined') {
        return;
      }
      const lecturaNumero = Number(valor);
      if (!Number.isFinite(lecturaNumero)) {
        invalidos.push({ medidorId, error: 'Lectura invalida' });
        return;
      }
      items.push({
        medidorId: Number(medidorId),
        lecturaActual: lecturaNumero,
        fechaLectura: fechaLecturaMasiva,
        observaciones: observacionesMasivas?.trim() || undefined,
      });
    });
    return { items, invalidos };
  };

  const handleGuardarMasivo = () => {
    const { items, invalidos } = construirPayloadMasivo();
    if (!items.length) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Sin lecturas',
        message: 'Complete al menos una lectura antes de guardar.',
        onConfirm: () => setModal({ ...modal, isOpen: false }),
      });
      return;
    }

    const fechaValida = isValidDateInputValue(fechaLecturaMasiva);
    if (!fechaValida) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Fecha invalida',
        message: 'Seleccione una fecha de lectura valida.',
        onConfirm: () => setModal({ ...modal, isOpen: false }),
      });
      return;
    }

    if (invalidos.length) {
      const nuevosErrores = {};
      invalidos.forEach((item) => {
        const medidorId = Number(item.medidorId);
        if (Number.isFinite(medidorId)) {
          nuevosErrores[medidorId] = item.error || 'Lectura invalida';
        }
      });
      setErroresMasivos((prev) => ({ ...prev, ...nuevosErrores }));
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Lecturas invalidas',
        message: 'Revise los valores marcados en rojo antes de continuar.',
        onConfirm: () => setModal({ ...modal, isOpen: false }),
      });
      return;
    }

    setModal({
      isOpen: true,
      type: 'warning',
      title: 'Confirmar carga',
      message: `Se guardaran ${items.length} lecturas con fecha ${fechaLecturaMasiva}.`,
      confirmText: 'Guardar',
      onConfirm: () => {
        setModal({ ...modal, isOpen: false });
        guardarMasivo(items);
      },
      onCancel: () => setModal({ ...modal, isOpen: false }),
    });
  };

  const guardarMasivo = async (items) => {
    setGuardandoMasivo(true);
    try {
      const response = await axios.post('/lecturas/masivas', { items });
      const resultados = response.data?.resultados || [];
      const nuevosErrores = {};
      const lecturasActualizadas = { ...ultimasLecturas };
      const valoresActualizados = { ...lecturasMasivas };
      let exitos = 0;
      let fallos = 0;

      resultados.forEach((resultado) => {
        const item = items[resultado.index];
        const medidorId = resultado.medidorId || item?.medidorId;
        if (resultado.status === 'ok') {
          exitos++;
          if (medidorId) {
            delete valoresActualizados[medidorId];
            lecturasActualizadas[medidorId] = {
              lecturaActual: item?.lecturaActual,
              fechaLectura: item?.fechaLectura || fechaLecturaMasiva,
            };
          }
        } else {
          fallos++;
          if (medidorId) {
            nuevosErrores[medidorId] = resultado.error || 'Error al guardar';
          }
        }
      });

      setLecturasMasivas(valoresActualizados);
      setErroresMasivos(nuevosErrores);
      setUltimasLecturas(lecturasActualizadas);

      if (fallos) {
        const detalles = Object.entries(nuevosErrores)
          .slice(0, 6)
          .map(([medidorId, mensaje]) => {
            const cliente = clientePorMedidor[medidorId];
            const etiqueta = cliente ? `${cliente.padron} - ${cliente.nombre}` : `Medidor ${medidorId}`;
            return `- ${etiqueta}: ${mensaje}`;
          })
          .join('\n');
        const detalleTexto = detalles ? `\n\nDetalle:\n${detalles}` : '';
        setModal({
          isOpen: true,
          type: 'warning',
          title: 'Carga parcial',
          message: `Se guardaron ${exitos} lecturas y ${fallos} quedaron con error.${detalleTexto}`,
          onConfirm: () => setModal({ ...modal, isOpen: false }),
        });
      } else {
        setModal({
          isOpen: true,
          type: 'success',
          title: 'Lecturas guardadas',
          message: `Se guardaron ${exitos} lecturas correctamente.`,
          onConfirm: () => setModal({ ...modal, isOpen: false }),
        });
      }
    } catch (error) {
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Error al guardar',
        message: error.response?.data?.message || 'No se pudieron guardar las lecturas.',
        onConfirm: () => setModal({ ...modal, isOpen: false }),
      });
    } finally {
      setGuardandoMasivo(false);
    }
  };

  const getEstadoBadge = (cliente) => {
    let estado = 'activo';
    const badgeClasses = {
      activo: 'bg-active-green/10 text-active-green',
      inactivo: 'bg-inactive-gray/10 text-inactive-gray',
      baja: 'bg-red-100 text-red-700',
    };
    const labels = {
      activo: 'Activo',
      inactivo: 'Inactivo',
      baja: 'Baja servicio',
    };

    if (cliente.servicio_dado_de_baja) {
      estado = 'baja';
    } else if (cliente.activo === false) {
      estado = 'inactivo';
    }

    return { class: badgeClasses[estado], label: labels[estado] };
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = useMemo(
    () => filteredClientes.slice(indexOfFirstItem, indexOfLastItem),
    [filteredClientes, indexOfFirstItem, indexOfLastItem]
  );
  const totalPages = Math.ceil(filteredClientes.length / itemsPerPage);
  const currentMedidorKey = useMemo(() => {
    if (!modoExpress) return '';
    const ids = currentItems
      .map((cliente) => cliente?.medidor?.id)
      .filter((id) => Number.isFinite(id));
    return ids.join(',');
  }, [modoExpress, currentItems]);

  useEffect(() => {
    if (!modoExpress || !currentMedidorKey) return;
    const medidorIds = currentMedidorKey
      .split(',')
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
    cargarUltimasLecturas(medidorIds);
  }, [modoExpress, currentMedidorKey]);

  const handlePreviewFechas = async () => {
    if (!editarFechasZona || !editarFechasDesde || !editarFechasHasta) {
      setModal({
        isOpen: true, type: 'error', title: 'Error',
        message: 'Completá zona, fecha desde y fecha hasta.',
        onConfirm: () => setModal(m => ({ ...m, isOpen: false })),
      });
      return;
    }
    setCargandoPreviewFechas(true);
    setEditarFechasPreview(null);
    try {
      const res = await axios.get('/lecturas/fecha-masiva/preview', {
        params: { zonaId: editarFechasZona, fechaDesde: editarFechasDesde, fechaHasta: editarFechasHasta },
      });
      setEditarFechasPreview(res.data.total);
    } catch (error) {
      setModal({
        isOpen: true, type: 'error', title: 'Error',
        message: error?.response?.data?.message || 'Error al obtener vista previa.',
        onConfirm: () => setModal(m => ({ ...m, isOpen: false })),
      });
    } finally {
      setCargandoPreviewFechas(false);
    }
  };

  const handleAplicarFechas = async () => {
    if (!editarFechasNueva) {
      setModal({
        isOpen: true, type: 'error', title: 'Error',
        message: 'Ingresá la nueva fecha.',
        onConfirm: () => setModal(m => ({ ...m, isOpen: false })),
      });
      return;
    }
    setGuardandoFechas(true);
    try {
      const res = await axios.put('/lecturas/fecha-masiva', {
        zonaId: parseInt(editarFechasZona),
        fechaDesde: editarFechasDesde,
        fechaHasta: editarFechasHasta,
        nuevaFecha: editarFechasNueva,
      });
      const { exitos, errores } = res.data;
      setModal({
        isOpen: true, type: errores === 0 ? 'success' : 'warning', title: 'Resultado',
        message: `Se actualizaron ${exitos} mediciones correctamente.${errores > 0 ? ` ${errores} fallaron.` : ''}`,
        onConfirm: () => setModal(m => ({ ...m, isOpen: false })),
      });
      setEditarFechasPreview(null);
      setEditarFechasNueva('');
    } catch (error) {
      setModal({
        isOpen: true, type: 'error', title: 'Error',
        message: error?.response?.data?.message || 'Error al actualizar fechas.',
        onConfirm: () => setModal(m => ({ ...m, isOpen: false })),
      });
    } finally {
      setGuardandoFechas(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 overflow-auto">
        <div className="bg-white border-b">
          <div className="px-8 py-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Mediciones</h1>
              <p className="text-gray-600 mt-1">
                {modoExpress
                  ? 'Carga express de lecturas para varios clientes'
                  : 'Seleccione un cliente para ver su detalle y mediciones'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setModoExpress(false); setModoEditarFechas(false); }}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                  !modoExpress && !modoEditarFechas
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
              >
                Lista
              </button>
              <button
                onClick={() => { setModoExpress(true); setModoEditarFechas(false); }}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                  modoExpress
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
              >
                Carga express
              </button>
              <button
                onClick={() => { setModoExpress(false); setModoEditarFechas(true); setEditarFechasPreview(null); }}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                  modoEditarFechas
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
              >
                Editar fechas
              </button>
            </div>
          </div>
        </div>

        <div className="p-8 space-y-6">
          <div className="bg-white rounded-lg border p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="lg:col-span-2">
                <label className="flex flex-col w-full">
                  <div className="flex w-full flex-1 items-stretch rounded-lg h-12 bg-[#f0f3f4]">
                    <div className="text-[#617c89] flex items-center justify-center pl-4">
                      <span className="material-symbols-outlined">search</span>
                    </div>
                    <input
                      className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden text-[#111618] focus:outline-0 focus:ring-0 border-none bg-transparent h-full placeholder:text-[#617c89] px-2 text-base font-normal leading-normal"
                      placeholder="Buscar por nombre, padron, CUIT o direccion..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </label>
              </div>

              <div className="flex items-center gap-3">
                <select
                  value={zonaFilter}
                  onChange={(e) => setZonaFilter(e.target.value)}
                  className="flex h-12 w-full items-center justify-between gap-x-2 rounded-lg bg-[#f0f3f4] px-4 text-[#111618] border-none focus:outline-none focus:ring-0"
                >
                  <option value="todas">Zona: Todas</option>
                  {zonas.map((zona) => (
                    <option key={zona.id} value={zona.id}>
                      Zona: {zona.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <select
                  value={estadoFilter}
                  onChange={(e) => setEstadoFilter(e.target.value)}
                  className="flex h-12 w-full items-center justify-between gap-x-2 rounded-lg bg-[#f0f3f4] px-4 text-[#111618] border-none focus:outline-none focus:ring-0"
                >
                  <option value="todos">Estado: Todos</option>
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                  <option value="baja">Baja servicio</option>
                </select>
              </div>

              <div className="flex items-center gap-3">
                <select
                  value={ordenamiento}
                  onChange={(e) => setOrdenamiento(e.target.value)}
                  className="flex h-12 w-full items-center justify-between gap-x-2 rounded-lg bg-[#f0f3f4] px-4 text-[#111618] border-none focus:outline-none focus:ring-0"
                >
                  <option value="padron">Orden: Padrón</option>
                  <option value="nombre">Orden: Nombre</option>
                </select>
              </div>
            </div>
          </div>

          <InformeConsumoPanel zonas={zonas} />

          {modoEditarFechas ? (
            <div className="bg-white rounded-lg border p-6 space-y-6">
              <h3 className="font-semibold text-gray-900 text-lg">Edición masiva de fechas</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Zona</label>
                  <select
                    value={editarFechasZona}
                    onChange={(e) => { setEditarFechasZona(e.target.value); setEditarFechasPreview(null); }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Seleccionar zona...</option>
                    {zonas.map((zona) => (
                      <option key={zona.id} value={zona.id}>{zona.nombre}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha desde</label>
                  <input
                    type="date"
                    value={editarFechasDesde}
                    onChange={(e) => { setEditarFechasDesde(e.target.value); setEditarFechasPreview(null); }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha hasta</label>
                  <input
                    type="date"
                    value={editarFechasHasta}
                    onChange={(e) => { setEditarFechasHasta(e.target.value); setEditarFechasPreview(null); }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <button
                  onClick={handlePreviewFechas}
                  disabled={cargandoPreviewFechas || !editarFechasZona || !editarFechasDesde || !editarFechasHasta}
                  className="px-4 py-2 text-sm bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cargandoPreviewFechas ? 'Buscando...' : 'Ver cuántas mediciones'}
                </button>
              </div>

              {editarFechasPreview !== null && (
                <div className="border-t pt-6 space-y-4">
                  <div className={`flex items-center gap-3 px-4 py-3 rounded-lg ${editarFechasPreview === 0 ? 'bg-yellow-50 text-yellow-800' : 'bg-blue-50 text-blue-800'}`}>
                    <span className="material-symbols-outlined">info</span>
                    <span className="font-medium">
                      {editarFechasPreview === 0
                        ? 'No se encontraron mediciones en ese rango para esa zona.'
                        : `Se van a mover ${editarFechasPreview} medición${editarFechasPreview !== 1 ? 'es' : ''} a la nueva fecha.`}
                    </span>
                  </div>

                  {editarFechasPreview > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nueva fecha</label>
                        <input
                          type="date"
                          value={editarFechasNueva}
                          onChange={(e) => setEditarFechasNueva(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleAplicarFechas}
                          disabled={guardandoFechas || !editarFechasNueva}
                          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {guardandoFechas ? 'Aplicando...' : 'Aplicar'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : modoExpress ? (
            <div className="space-y-6">
              <div className="bg-white rounded-lg border p-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de lectura</label>
                      <input
                        type="date"
                        value={fechaLecturaMasiva}
                        onChange={(e) => setFechaLecturaMasiva(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Observaciones (opcional)</label>
                      <input
                        type="text"
                        value={observacionesMasivas}
                        onChange={(e) => setObservacionesMasivas(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Notas generales para esta carga"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={limpiarLecturasMasivas}
                      disabled={lecturasPendientes === 0 || guardandoMasivo}
                      className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Limpiar
                    </button>
                    <button
                      onClick={handleGuardarMasivo}
                      disabled={lecturasPendientes === 0 || guardandoMasivo}
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {guardandoMasivo ? 'Guardando...' : `Guardar ${lecturasPendientes} lecturas`}
                    </button>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
                  <span className="material-symbols-outlined text-base">info</span>
                  <p>Ingrese las lecturas en la tabla y confirme el guardado masivo.</p>
                </div>
              </div>

              <div className="bg-white rounded-lg border overflow-hidden">
                <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Carga express</h3>
                    <p className="text-xs text-gray-500 mt-1">{filteredClientes.length} resultados</p>
                  </div>
                  <span className="text-xs text-gray-500">
                    {cargandoUltimas ? 'Cargando ultimas lecturas...' : ''}
                  </span>
                </div>

                {loading ? (
                  <div className="p-8 text-center text-gray-500">Cargando clientes...</div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead className="bg-white">
                          <tr className="border-b">
                            <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Padron</th>
                            <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                            <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Zona</th>
                            <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                            <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Ultima lectura</th>
                            <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Nueva lectura</th>
                            <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Accion</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {currentItems.length === 0 ? (
                            <tr>
                              <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                                No hay clientes para mostrar con estos filtros.
                              </td>
                            </tr>
                          ) : (
                            currentItems.map((cliente) => {
                              const estadoBadge = getEstadoBadge(cliente);
                              const medidorId = cliente?.medidor?.id;
                              const bloqueo = obtenerBloqueoCliente(cliente);
                              const ultimaLectura = medidorId ? ultimasLecturas[medidorId] : null;
                              const error = medidorId ? erroresMasivos[medidorId] : null;
                              const lecturaValor = medidorId ? (lecturasMasivas[medidorId] ?? '') : '';
                              return (
                                <tr key={cliente.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{cliente.padron}</td>
                                  <td className="px-6 py-4 text-sm text-gray-700">{cliente.nombre}</td>
                                  <td className="px-6 py-4 text-sm text-gray-700">{cliente.zona?.nombre || '-'}</td>
                                  <td className="px-6 py-4 text-sm">
                                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${estadoBadge.class}`}>
                                      {estadoBadge.label}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-700">
                                    {ultimaLectura ? (
                                      <div className="flex flex-col gap-1">
                                        <span className="font-medium">{ultimaLectura.lecturaActual} m3</span>
                                        <span className="text-xs text-gray-500">
                                          {ultimaLectura.fechaLectura
                                            ? formatApiDate(ultimaLectura.fechaLectura)
                                            : '-'}
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="text-xs text-gray-500">{cargandoUltimas ? 'Cargando...' : '-'}</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 text-sm">
                                    {bloqueo ? (
                                      <span className="text-xs text-gray-500">{bloqueo}</span>
                                    ) : (
                                      <div className="flex flex-col gap-1">
                                        <input
                                          type="text"
                                          inputMode="numeric"
                                          value={lecturaValor}
                                          onChange={(e) => handleLecturaMasivaChange(medidorId, e.target.value)}
                                          className={`w-28 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            error ? 'border-error' : 'border-gray-300'
                                          }`}
                                          placeholder="000000"
                                          disabled={guardandoMasivo}
                                        />
                                        {error && (
                                          <span className="text-xs text-error">{error}</span>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 text-center">
                                    <button
                                      onClick={() => navigate(`/administrativo/mediciones/${cliente.id}`)}
                                      className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                                    >
                                      Ver
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    {totalPages > 1 && (
                      <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
                        <span className="text-sm text-gray-600">
                          Pagina {currentPage} de {totalPages}
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            className="px-3 py-2 text-sm bg-white border rounded-lg hover:bg-gray-100 disabled:opacity-50"
                            disabled={currentPage === 1}
                          >
                            Anterior
                          </button>
                          <button
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                            className="px-3 py-2 text-sm bg-white border rounded-lg hover:bg-gray-100 disabled:opacity-50"
                            disabled={currentPage === totalPages}
                          >
                            Siguiente
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Clientes</h3>
                <span className="text-sm text-gray-500">{filteredClientes.length} resultados</span>
              </div>

              {loading ? (
                <div className="p-8 text-center text-gray-500">Cargando clientes...</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className="bg-white">
                        <tr className="border-b">
                          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Padron</th>
                          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Zona</th>
                          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Accion</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {currentItems.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                              No hay clientes para mostrar con estos filtros.
                            </td>
                          </tr>
                        ) : (
                          currentItems.map((cliente) => {
                            const estadoBadge = getEstadoBadge(cliente);
                            return (
                              <tr key={cliente.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm font-medium text-gray-900">{cliente.padron}</td>
                                <td className="px-6 py-4 text-sm text-gray-700">{cliente.nombre}</td>
                                <td className="px-6 py-4 text-sm text-gray-700">{cliente.zona?.nombre || '-'}</td>
                                <td className="px-6 py-4 text-sm">
                                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${estadoBadge.class}`}>
                                    {estadoBadge.label}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-center">
                                  <button
                                    onClick={() => navigate(`/administrativo/mediciones/${cliente.id}`)}
                                    className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                  >
                                    Ver mediciones
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
                      <span className="text-sm text-gray-600">
                        Pagina {currentPage} de {totalPages}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          className="px-3 py-2 text-sm bg-white border rounded-lg hover:bg-gray-100 disabled:opacity-50"
                          disabled={currentPage === 1}
                        >
                          Anterior
                        </button>
                        <button
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          className="px-3 py-2 text-sm bg-white border rounded-lg hover:bg-gray-100 disabled:opacity-50"
                          disabled={currentPage === totalPages}
                        >
                          Siguiente
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={modal.isOpen}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onConfirm={modal.onConfirm}
        onCancel={() => setModal({ ...modal, isOpen: false })}
      />
    </div>
  );
};

export default MedicionesAdmin;
